import { useCallback } from 'react';
import { pick } from 'ramda';
import { useDispatch } from 'react-redux';
import { useAuthSelector, useUserSelector } from 'js/store/hooks';
import { userSetAttributesAction } from 'js/hooks/user/store/actions';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { showReCaptchaPopupAction, hideReCaptchaPopupAction } from 'js/components/popups/ReCaptchaPopup';

import * as pas from '../pas';
import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { useLoginRequest } from './useLoginRequest';
import { useValidateSession } from './useValidateSession';
import { useBeforeLoginAction } from './useBeforeLoginAction';
import { useContinueLoginWithSessionToken } from './useContinueLoginWithSessionToken';

export const useGenericLogin = () => {
    const dispatch = useDispatch();
    const request = useLoginRequest();
    const validateSession = useValidateSession();
    const beforeLoginAction = useBeforeLoginAction();
    const continueLoginWithSessionToken = useContinueLoginWithSessionToken();
    const { role, isLoggedIn } = useUserSelector(pick(['role', 'isLoggedIn']));
    const { isLoginInProcess, isLogoutInProcess } = useAuthSelector(pick(['isLoginInProcess', 'isLogoutInProcess']));

    return useCallback(
        async function genericLogin(options: {
            inputUserName?: string | null;
            inputPassword: string;
            isRememberMeLogin?: boolean;
            beforePASLoginCallback?: () => void;
            sessionValidationErrorCallback?: () => void;
            isAfterRegisterLogin?: boolean;
            isAfterLoginWithCredentials?: boolean;
            isLoginToNativeAppEnabled?: boolean;
            isRecaptchaEnabled?: boolean;
            isAutoLogin?: boolean;
            loginMode: string;
        }) {
            const {
                inputUserName = null, inputPassword, isRememberMeLogin = false,
                isAfterRegisterLogin = false, loginMode, beforePASLoginCallback,
                isAfterLoginWithCredentials = false, sessionValidationErrorCallback,
                isLoginToNativeAppEnabled = false, isRecaptchaEnabled = false, isAutoLogin = false
            } = options;

            if (isLoggedIn) {
                return;
            }

            if (role === 'admin') {
                return Promise.reject({ errorMessage: 'Admin can\'t login to portal' });
            }

            if (isLoginInProcess || isLogoutInProcess || !request) {
                return Promise.reject();
            }

            dispatch(actions.setIsLoginInProcess(true));

            beforeLoginAction();

            // PAS section
            try {
                const { languageCode } = request;
                const initialCredentials = {
                    loginMode,
                    languageCode,
                    userName: inputUserName,
                    password: inputPassword,
                    rememberMeLogin: isRememberMeLogin
                };

                beforePASLoginCallback?.();

                const pasLoginResponse = await pas.login(initialCredentials);

                const { userId, loginName, userName, sessionToken } = helpers.parsePasLoginResponse(pasLoginResponse);
                const credentials = { ...initialCredentials, userName, loginName, userId };

                const isSessionValid = validateSession(pasLoginResponse, credentials, {
                    afterRegisterLogin: isAfterRegisterLogin, isAfterLoginWithCredentials, isLoginToNativeAppEnabled, isAutoLogin
                });

                if (!isSessionValid) {
                    dispatch(actions.setIsLoginInProcess(false));
                    isAutoLogin && dispatch(actions.setAutoLoginCompleted());
                    sessionValidationErrorCallback?.();

                    return Promise.reject(pasLoginResponse);
                }

                if (!sessionToken) {
                    dispatch(actions.setIsLoginInProcess(false));
                    isAutoLogin && dispatch(actions.setAutoLoginCompleted());
                    publicEventsAPIService.trigger('auth:generic_login:error', pasLoginResponse);

                    return Promise.reject(pasLoginResponse);
                }

                dispatch(userSetAttributesAction({
                    loginName, userId, userName, password: inputPassword, rememberMeLogin: isRememberMeLogin
                }));

                if (isLoginToNativeAppEnabled) {
                    try {
                        await helpers.externalLoginAction({
                            ...credentials, sessionToken, afterRegisterLogin: isAfterRegisterLogin
                        });
                    } catch (error) {
                        dispatch(actions.setIsLoginInProcess(false));
                        isAutoLogin && dispatch(actions.setAutoLoginCompleted());

                        return Promise.reject(error);
                    }
                }

                // OAPI section
                try {
                    await continueLoginWithSessionToken({
                        ...credentials,
                        sessionToken,
                        isAutoLogin,
                        isLoginToNativeAppEnabled,
                        isAfterLoginWithCredentials,
                        afterRegisterLogin: isAfterRegisterLogin
                    });
                } catch (error) {
                    // The catch block will be processed in useContinueLoginWithSessionToken
                } finally {
                    isAutoLogin && dispatch(actions.setAutoLoginCompleted());
                }
            } catch (error: any) {
                isAutoLogin && dispatch(actions.setAutoLoginCompleted());

                if (isRecaptchaEnabled && error?.data?.errorCode === 301) {
                    dispatch(showReCaptchaPopupAction({
                        operation: 'login',
                        onReCaptchaTokenValidate: () => {
                            dispatch(hideReCaptchaPopupAction());
                            genericLogin(options);
                        },
                        onReCaptchaTokenError: () => {
                            dispatch(hideReCaptchaPopupAction());
                        },
                        onClose: () => {
                            dispatch(actions.setIsLoginInProcess(false));
                            publicEventsAPIService.trigger('auth:generic_login:error', error);
                        }
                    }));
                } else {
                    dispatch(actions.setIsLoginInProcess(false));
                    publicEventsAPIService.trigger('auth:generic_login:error', error);

                   return Promise.reject(error);
                }
            }
        },
        [
            isLoggedIn, role, isLoginInProcess, isLogoutInProcess, request,
            dispatch, beforeLoginAction, validateSession, continueLoginWithSessionToken
        ]
    );
};
